'use client';
import { useId, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Pagination } from 'swiper/modules';
import NextImage from 'next/image';
import { type TestimonialCardsProps } from './index';
import { Icon, Link, Tag } from 'ui';

const TestimonialCardsMobile = ({ cards }: TestimonialCardsProps) => {
  const id = useId().replaceAll(':', ''); // Remove colons as an SSR-friendly alternative to CSS.escape()
  const [totalSlides, setTotalSlides] = useState<number>(cards.length);
  const [currentSlide, setCurrentSlide] = useState<number>(1);
  const cardCount = cards.length;

  // Render null if less than 2 cards
  if (cardCount < 2) {
    return null;
  }

  return (
    <div className="lg:hidden">
      <Swiper
        modules={[A11y, Pagination]}
        pagination={{
          el: `#${id}-pagination`,
          type: 'bullets',
          clickable: true,
          bulletClass:
            'size-3 bg-stone-50 rounded-full opacity-50 transition-colors cursor-pointer',
          bulletActiveClass: 'opacity-100',
        }}
        spaceBetween={24}
        slidesPerView={1.15}
        breakpoints={
          {
            // when window width is >= 360px
            360: {
              slidesPerView: 1.25,
            },
            // when window width is >= 480px
            480: {
              slidesPerView: 1.5,
            },
            // Only add 800px and 1024px breakpoints if there are 3 cards
            ...(cardCount === 3 && {
              // when window width is >= 800px
              800: {
                slidesPerView: 2.25,
              },
              // when window width is >= 1024px
              1024: {
                slidesPerView: 2.75,
              },
            }),
          } as { [width: number]: { slidesPerView: number } }
        }
        onPaginationUpdate={(swiper) => {
          setTotalSlides(swiper.pagination.bullets.length);
        }}
        onSlideChange={(swiper) => {
          setCurrentSlide(swiper.activeIndex + 1);
        }}
      >
        {cards.map((card, index) => (
          <SwiperSlide key={index} className="w-fit">
            <article className="relative h-148 flex flex-col">
              {/* Image */}
              <div className="relative w-full h-72 flex flex-col justify-end p-5">
                <NextImage
                  src={card.image.src}
                  alt={card.image.alt || ''}
                  fill={true}
                  className="absolute w-full h-auto object-cover rounded-t-lg"
                  role="presentation"
                />

                {/* Logo */}
                {card.logo && (
                  <>
                    <NextImage
                      src={card.logo.src}
                      alt={`${card.authorOrg || 'Company'} logo`}
                      width={card.logo.width}
                      height={card.logo.height}
                      className="relative z-3 max-w-full h-auto"
                    />

                    {/* Color Gradient */}
                    <div
                      className="absolute z-2 bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-black/65 to-transparent"
                      aria-hidden="true"
                    />
                  </>
                )}
              </div>

              {/* Content */}
              <div className="grow p-6 flex flex-col justify-end gap-5 bg-stone-800 rounded-b-lg">
                <div className="grow">
                  {!!card.tag && <Tag>{card.tag}</Tag>}
                </div>

                <blockquote className="flex flex-col gap-4">
                  <p className="text-p2">
                    {!card.hideLeftQuote && (
                      <>
                        &ldquo; <br />
                      </>
                    )}
                    {card.content}
                    {!card.hideRightQuote && <>&rdquo;</>}
                  </p>

                  <footer className="flex flex-col gap-6">
                    {(!!card.author ||
                      !!card.authorTitle ||
                      !!card.authorOrg) && (
                      <cite className="text-p3 font-bold not-italic text-neutral-200 flex flex-col opacity-85">
                        {(card.author || card.authorTitle) && (
                          <span>
                            {card.author && <span>{card.author}</span>}
                            {card.authorTitle && (
                              <>
                                {card.author && ', '}
                                <span>{card.authorTitle}</span>
                              </>
                            )}
                            {card.authorOrg && (
                              <>
                                {(card.author || card.authorTitle) && <br />}
                                <span>{card.authorOrg}</span>
                              </>
                            )}
                          </span>
                        )}
                      </cite>
                    )}

                    {!!card.link?.href && (
                      <div className="text-p3">
                        <Link
                          href={card.link.href}
                          target={card.link.target}
                          variant="neutral"
                        >
                          <span>{card.link.text}</span>
                          <Icon
                            name="carousel-arrow-right"
                            className="inline-block align-middle ml-1.5 w-4 h-auto"
                          />
                        </Link>
                      </div>
                    )}
                  </footer>
                </blockquote>
              </div>
            </article>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="w-full flex justify-between items-center gap-3 mt-8 pr-wrapper-page-full-x">
        <p>
          {currentSlide} / {totalSlides}
        </p>

        {/* Custom Pagination */}
        <div
          id={`${id}-pagination`}
          className="height-3 flex justify-end gap-4"
        />
      </div>
    </div>
  );
};

export default TestimonialCardsMobile;
